import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Crown, Users, Building2, Warehouse, Zap, Clock, CreditCard, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [selecting, setSelecting] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [contactEmail, setContactEmail] = useState('marlene@thrivebeekeeping.com');

  const fetchData = useCallback(async () => {
    try {
      // Plans are public, status requires auth
      const plansRes = await fetch(`${API}/api/subscription/plans`);
      if (plansRes.ok) {
        setPlans(await plansRes.json());
      }

      if (token) {
        const statusRes = await fetch(`${API}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (statusRes.ok) {
          setCurrentStatus(await statusRes.json());
        }
      }
    } catch (error) {
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }

    // Fetch FAQs and contact email (non-blocking)
    try {
      const faqRes = await fetch(`${API}/api/subscription/faqs`);
      if (faqRes.ok) {
        const faqData = await faqRes.json();
        if (faqData.length > 0) setFaqs(faqData);
      }
      const settingsRes = await fetch(`${API}/api/app-settings`);
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        if (settings.contact_email) setContactEmail(settings.contact_email);
      }
    } catch { /* non-critical */ }
  }, [token]);

  // Poll for payment status after Stripe redirect
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const res = await fetch(`${API}/api/subscription/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await res.json();

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success(data.message || 'Payment successful! Your subscription is now active.');
        // Clear URL params and refresh data
        setSearchParams({});
        fetchData();
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setCheckingPayment(false);
        toast.error('Error checking payment status. Please refresh the page.');
      }
    }
  }, [token, fetchData, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (cancelled) {
      toast.info('Payment cancelled. You can try again when ready.');
      setSearchParams({});
    } else if (sessionId && success) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus, setSearchParams]);

  // Create Stripe checkout session
  const handleStripeCheckout = async (planId, plan) => {
    // Free plan - activate directly without Stripe
    if (plan.price_monthly === 0 && plan.price_annual === 0) {
      setSelecting(true);
      try {
        const res = await fetch(`${API}/api/subscription/activate-free`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: planId })
        });
        if (res.ok) {
          toast.success(`${plan.name} plan activated!`);
          fetchData();
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Failed to activate plan');
        }
      } catch (error) {
        toast.error('Failed to activate plan');
      } finally {
        setSelecting(false);
      }
      return;
    }

    // Paid plan - check Stripe config
    const stripePriceId = billingCycle === 'monthly' 
      ? plan.stripe_price_id_monthly 
      : plan.stripe_price_id_annual;

    if (!stripePriceId) {
      toast.error(`Stripe payment not configured for ${billingCycle} billing on this plan. Please contact support.`);
      return;
    }

    setSelecting(true);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(
        `${API}/api/subscription/checkout?plan_id=${planId}&billing_cycle=${billingCycle}&origin_url=${encodeURIComponent(originUrl)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          // Redirect to Stripe Checkout
          window.location.href = data.checkout_url;
        } else {
          toast.error('Failed to get checkout URL');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Failed to initiate payment');
    } finally {
      setSelecting(false);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'hobby': return <Crown className="h-6 w-6" />;
      case 'club': return <Users className="h-6 w-6" />;
      case 'association': return <Building2 className="h-6 w-6" />;
      case 'large_club': return <Warehouse className="h-6 w-6" />;
      default: return <Crown className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'hobby': return 'amber';
      case 'club': return 'blue';
      case 'association': return 'purple';
      case 'large_club': return 'emerald';
      default: return 'amber';
    }
  };

  const isCurrentPlan = (planId) => {
    if (!currentStatus) return false;
    const userPlan = currentStatus.plan || 'free';
    if (userPlan === planId) return true;
    // Map "free" or "trial" to the first free-tier plan (e.g., "hobby" = Backyard Beekeeper at $0)
    if ((userPlan === 'free' || userPlan === 'trial') && planId === 'hobby') return true;
    return false;
  };

  const isUpgrade = (planId) => {
    const planOrder = ['trial', 'hobby', 'club', 'association', 'large_club'];
    const currentIndex = planOrder.indexOf(currentStatus?.plan || 'trial');
    const targetIndex = planOrder.indexOf(planId);
    return targetIndex > currentIndex;
  };

  // Check if plan has Stripe configured
  const isFree = (plan) => plan.price_monthly === 0 && plan.price_annual === 0;

  const hasStripeConfig = (plan) => {
    if (isFree(plan)) return true; // Free plans don't need Stripe
    return billingCycle === 'monthly' 
      ? !!plan.stripe_price_id_monthly 
      : !!plan.stripe_price_id_annual;
  };

  if (loading || checkingPayment) {
    return (
      <div className="bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          {checkingPayment ? (
            <>
              <Loader2 className="h-12 w-12 text-green-600 animate-spin mx-auto mb-4" />
              <p className="text-stone-800 font-medium">Verifying your payment...</p>
              <p className="text-stone-500 text-sm mt-2">Please wait while we confirm your subscription.</p>
            </>
          ) : (
            <>
              <CreditCard className="h-12 w-12 text-amber-600 animate-pulse mx-auto mb-4" />
              <p className="text-stone-600">Loading subscription...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 p-4">
      {/* Header */}
      <div className="max-w-7xl mb-6">
        <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-amber-600" />
          Manage Subscription
        </h1>
        <p className="text-sm text-stone-500 mt-1">Choose the plan that fits your beekeeping needs</p>
      </div>

      <div className="max-w-7xl">
        {/* Current Status Banner */}
        {currentStatus && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${
                    currentStatus.status === 'trial' ? 'bg-blue-100' :
                    currentStatus.status === 'active' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {currentStatus.status === 'trial' ? <Clock className="h-6 w-6 text-blue-600" /> :
                     currentStatus.status === 'active' ? <Check className="h-6 w-6 text-green-600" /> :
                     <AlertTriangle className="h-6 w-6 text-red-600" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">
                      {currentStatus.status === 'trial' ? '🎉 Free Trial' :
                       currentStatus.status === 'active' ? `${currentStatus.plan_display_name || (currentStatus.plan ? currentStatus.plan.charAt(0).toUpperCase() + currentStatus.plan.slice(1) : 'Active')}` :
                       'Subscription Expired'}
                    </h3>
                    <p className="text-sm text-stone-500">
                      {currentStatus.status === 'trial' && currentStatus.trial_days_left > 0 
                        ? `${currentStatus.trial_days_left} days remaining in trial`
                        : currentStatus.status === 'trial' 
                        ? 'Trial expired - please subscribe'
                        : currentStatus.status === 'active'
                        ? `${currentStatus.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} billing`
                        : 'Please renew to continue'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="font-bold text-2xl text-stone-800">{currentStatus.hive_count}</p>
                    <p className="text-stone-500">of {currentStatus.max_hives} hives</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-2xl text-stone-800">{currentStatus.member_count || currentStatus.crew_count || 0}</p>
                    <p className="text-stone-500">{(currentStatus.max_crew ?? currentStatus.max_members ?? 0) > 0 ? `of ${currentStatus.max_crew ?? currentStatus.max_members} crew` : 'crew (owner only)'}</p>
                  </div>
                  {!currentStatus.can_add_hives && (
                    <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-medium">
                      Hive limit reached
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-xl p-1 shadow-sm border border-stone-200 inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                Save ~17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const planId = plan.plan_id || plan.id;
            const color = getPlanColor(planId);
            const isCurrent = isCurrentPlan(planId);
            const isUpgradeOption = isUpgrade(planId);
            
            return (
              <Card 
                key={planId} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${
                  isCurrent ? 'ring-2 ring-green-500' : ''
                } ${plan.is_popular ? 'md:scale-105 z-10' : ''}`}
              >
                {plan.is_popular && (
                  <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute top-0 left-0 right-0 bg-green-600 text-white text-center text-xs font-bold py-1.5 tracking-wide">
                    YOUR CURRENT PLAN
                  </div>
                )}
                
                <CardHeader className={`bg-${color}-50 border-b border-${color}-100 ${isCurrent ? 'pt-10' : ''}`}>
                  {plan.icon ? (
                    <img
                      src={plan.icon.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL || ''}${plan.icon}` : plan.icon}
                      alt=""
                      className="w-12 h-12 object-contain rounded-xl mb-3"
                    />
                  ) : (
                    <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center text-${color}-600 mb-3`}>
                      {getPlanIcon(planId)}
                    </div>
                  )}
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <p className="text-sm text-stone-500">{plan.description}</p>
                </CardHeader>
                
                <CardContent className="p-6">
                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-stone-800">
                        ${billingCycle === 'annual' ? plan.price_annual : plan.price_monthly?.toFixed(2)}
                      </span>
                      <span className="text-stone-500">
                        /{billingCycle === 'annual' ? 'year' : 'month'}
                      </span>
                    </div>
                    {billingCycle === 'annual' && plan.price_annual > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ${(plan.price_annual / 12).toFixed(2)}/month • 2 months free
                      </p>
                    )}
                  </div>

                  {/* Hive Range */}
                  <div className="bg-stone-50 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-stone-700">📦 {plan.hive_range || `Up to ${plan.max_hives} hives`}</p>
                    <p className="text-xs text-stone-500">👥 {(plan.max_crew ?? plan.max_members ?? 0) > 0 ? `Up to ${plan.max_crew ?? plan.max_members} crew member${(plan.max_crew ?? plan.max_members) > 1 ? 's' : ''}` : 'Owner only (no crew)'}</p>
                    {plan.max_inspections && (
                      <p className="text-xs text-stone-500">📝 {plan.max_inspections} inspections/period</p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className={`h-4 w-4 text-${color}-500 mt-0.5 flex-shrink-0`} />
                        <span className="text-stone-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleStripeCheckout(planId, plan)}
                    disabled={selecting || isCurrent || !hasStripeConfig(plan)}
                    className={`w-full ${
                      isCurrent 
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : !hasStripeConfig(plan)
                        ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                        : isUpgradeOption
                        ? `bg-${color}-600 hover:bg-${color}-700 text-white`
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                  >
                    {selecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrent ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Current Plan
                      </>
                    ) : !hasStripeConfig(plan) ? (
                      'Coming Soon'
                    ) : isFree(plan) ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Activate Free
                      </>
                    ) : isUpgradeOption ? (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Subscribe Now
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        {plan.button_text || 'Subscribe'}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ / Info Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(faqs.length > 0 ? faqs : [
              { question: 'What happens when I reach my hive limit?', answer: 'You will be prompted to upgrade to a higher plan. Your existing data remains safe.' },
              { question: 'Can I cancel anytime?', answer: 'Yes, you can cancel your subscription at any time. Your data will be preserved and you can export it.' },
            ]).map((faq, i) => (
              <div key={faq.id || i}>
                <h4 className="font-medium text-stone-800">{faq.question}</h4>
                <p className="text-sm text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Contact Support */}
        <div className="text-center mt-8 pb-8">
          <p className="text-stone-500 text-sm">
            Need a custom plan for your organization?{' '}
            <a href={`mailto:${contactEmail}`} className="text-amber-600 hover:underline">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
